//* Libraries imports
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion"; 

//* Components imports
import { ProcessList } from "./process-list";
import { ErrorMessages } from "../../../../component/common/ErrorMessages";
import { ProcessListSkeleton } from "../../../../skeleton/ProcessListSkeleton";

//* Hooks imports
import { useGetPaginatedProcess } from "../../../../hooks/queries/process/use-get-paginated-process";

export function Content() {
  const navigate = useNavigate();  
  const [searchParams] = useSearchParams();
  
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [validatedSearchTerm, setValidatedSearchTerm] = useState(searchParams.get("search") || "");
  const page = parseInt(searchParams.get("page") || "1", 10);
  const perPage = 10;
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const processos = useGetPaginatedProcess(page, perPage, validatedSearchTerm);

  useEffect(() => {
    const urlSearchTerm = searchParams.get("search") || "";
    setValidatedSearchTerm(urlSearchTerm);
  }, [searchParams]);

  useEffect(() => {
    if (processos.isError && processos.error) {
      const error = processos.error as any; 
      const message = typeof error === 'string' ? error : error?.message || "Ocorreu um erro ao buscar os processos.";
      setErrorMessages([message]);
    }
  }, [processos.isError, processos.error]);

  const clearErrorMessages = () => {
    setErrorMessages([]);
  };


  const processFields = [
    { name: "name", header: "Nome", type: "text" as const },
    { name: "start", header: "Data de Início", type: "date" as const },
    { name: "end", header: "Data de Fim", type: "date" as const },
    { name: "online", header: "Online", type: "boolean" as const },
    { name: "description", header: "Descrição", type: "text" as const },
  ];  
  
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  useEffect(() => {
    if (searchTerm === "" && validatedSearchTerm !== "") {
      setValidatedSearchTerm("");
      const newSearchParams = new URLSearchParams();
      newSearchParams.set("page", "1");
      navigate(`/?${newSearchParams.toString()}`);
    }
  }, [searchTerm, validatedSearchTerm, navigate]);

  const handleSearch = () => {
    if (searchTerm.length >= 3 || searchTerm.length === 0) {
      setValidatedSearchTerm(searchTerm);
      const newSearchParams = new URLSearchParams();
      if (searchTerm) {
        newSearchParams.set("search", searchTerm);
      }
      newSearchParams.set("page", "1");
      navigate(`/?${newSearchParams.toString()}`);
    }
  };

  const handlePageChange = (newPage: number) => {
    const newSearchParams = new URLSearchParams(window.location.search);
    newSearchParams.set("page", newPage.toString());
    navigate(`/process?${newSearchParams.toString()}`);
  };

  const handleViewProcess = (id: number) => {
    navigate(`/process/${id}`);
  };
  
  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <motion.div 
      className="w-100"
      initial="hidden"
      animate="visible"
      variants={sectionVariants}
    >
      <ErrorMessages messages={errorMessages} clearErrors={clearErrorMessages} />
      <motion.section 
        className="pt-4 p-2"
        variants={sectionVariants} 
      >
        <div className="d-flex justify-content-between align-items-center">
          <div className="text-center text-sm-start">
            <motion.h2 
              className="fs-1 fs-sm-2 fs-md-1 fw-bold mb-0"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              style={{ wordBreak: 'break-word' }}
            >
              Processos Seletivos
            </motion.h2>
            <motion.p 
              className="text-muted small mb-0 d-none d-sm-block"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              Gerencie todos os processos seletivos
            </motion.p>
            <motion.p 
              className="text-muted smaller mb-0 d-block d-sm-none"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              Gerenciar processos
            </motion.p>
          </div>
          <motion.div 
            className="d-flex align-items-center bg-light rounded px-3 py-2 shadow-sm"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            {processos.isLoading ? (
              <div className="me-2" style={{ width: 20, height: 20 }} /> 
            ) : processos.isError ? (
              <span className="text-danger">Erro ao carregar totais!</span>
            ) : (
              <div className="d-flex align-items-center">
                <span className="text-secondary me-2">Total:</span>
                <motion.span 
                  className="badge bg-primary rounded-pill fs-6"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 10, delay: 0.4 }}
                >
                  {processos.data?.meta?.totalItems || 0}
                </motion.span>
              </div>
            )}
          </motion.div>
        </div>
      </motion.section>

      {processos.isLoading ? (
        <ProcessListSkeleton />
      ) : processos.isError ? (
        <motion.div 
          className="d-flex flex-column justify-content-center align-items-center text-danger p-4" 
          style={{ minHeight: '200px' }}
          initial="hidden"
          animate="visible"
          variants={sectionVariants}
        >
          <span className="fs-4 mb-2">Oops!</span>
          <p className="text-center">
            Ocorreu um erro ao carregar os processos seletivos. <br/>
            {errorMessages.length > 0 && (<>Detalhe: {errorMessages[0]}<br/></>)}
            Por favor, tente novamente mais tarde.
          </p>
        </motion.div>
      ) : (
        <motion.section 
          className="pt-2"
          variants={sectionVariants} 
        >
          <ProcessList
            title="Processos Seletivos"
            pronoun="os"
            searchPlaceholder="Pesquisar processo..."
            notFoundMessage="Nenhum processo encontrado."
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onSearch={handleSearch}
            onViewProcess={handleViewProcess}            
            fields={processFields}
            items={processos.data?.data || []}
            loading={processos.isLoading} 
            currentPage={page}
            totalPages={processos.data?.meta?.totalPages || 1}
            totalItems={processos.data?.meta?.totalItems || 0}
            itemsPerPage={perPage}
            onPageChange={handlePageChange}
          />
        </motion.section>
      )}
    </motion.div>
  );
}
